import React, { useState } from 'react'
import { FaCheckCircle, FaRegSquare, FaCheckSquare } from 'react-icons/fa'
import { Banner, Bar, Field, FoBtn, KV, Pill, ScreenShell, Section, TextArea } from './fieldUi'
import { useField } from './fieldContext'
import { clockLabel } from '../geo'
import { lb } from '../FieldOpsKit'

/**
 * Start of the day. Salesperson: a quick readiness check (location, offline data, optional odometer). Driver: the load
 * checklist (every invoice on the truck), the weight against the vehicle capacity and the pre-trip check.
 */
export default function StartDayScreen() {
    const f = useField()
    const { state, nav, actor, stops, summary, gps, date, online } = f
    const isDriver = actor.kind === 'driver'
    const drv = isDriver ? state.drivers.drivers.filter((d) => d.id === actor.driverId)[0] : null
    const veh = drv ? state.drivers.vehicles.filter((v) => v.id === drv.vehicleId)[0] : null
    const [odo, setOdo] = useState('')
    const [loaded, setLoaded] = useState({})
    const [tires, setTires] = useState(true)
    const [fuel, setFuel] = useState(true)
    const [damage, setDamage] = useState(false)
    const [note, setNote] = useState('')
    const weight = stops.reduce((a, s) => a + (s.weightLb || 0), 0)
    const cap = veh ? veh.capacityLb : 0
    const over = isDriver && cap > 0 && weight > cap
    const loadedCount = stops.filter((s) => loaded[s.id]).length
    const allLoaded = isDriver ? loadedCount === stops.length : true
    const vehicleOk = tires && fuel && !damage

    const start = () => {
        f.dispatch({ type: 'DAY_START', actorKey: actor.key, date: date, odo: odo === '' ? undefined : Number(odo), loadChecked: isDriver ? loadedCount : undefined, vehicleOk: vehicleOk, vehicleNote: note })
        if (!vehicleOk) f.send({ type: 'CHAT_SEND', actorKey: actor.key, from: 'worker', by: actor.name, text: 'Vehicle check problem before leaving: ' + [!tires ? 'tires/lights' : '', !fuel ? 'fuel' : '', damage ? 'damage' : ''].filter(Boolean).join(', ') + '. ' + note, topic: 'incident' })
        f.toast('Day started. Have a safe route!')
        nav.pop()
    }

    return (
        <ScreenShell title="Start my day" subtitle={stops.length + (isDriver ? ' deliveries' : ' visits') + ' · about ' + summary.miles + ' mi of driving'} onBack={nav.pop}
            footer={<React.Fragment><FoBtn kind="ghost" onClick={nav.pop}>Not yet</FoBtn><FoBtn kind="success" big onClick={start} disabled={isDriver && !allLoaded}>{isDriver && !allLoaded ? 'Check the load first' : 'Start day'}</FoBtn></React.Fragment>}>
            <Section title="Today">
                <KV label="First stop">{stops[0] ? stops[0].name : '—'}</KV>
                <KV label="Planned finish">{summary.endEta ? clockLabel(summary.endEta) : '—'}</KV>
                <KV label="Driving (estimate)">{summary.miles} mi</KV>
                <KV label="Location">{gps.source === 'denied' ? <Pill tone="red">Off</Pill> : gps.pos ? <Pill tone="green">Working</Pill> : <Pill tone="amber">Looking for signal…</Pill>}</KV>
                <KV label="Connection">{online ? <Pill tone="green">Online</Pill> : <Pill tone="amber">Offline — data saved on the phone</Pill>}</KV>
                {!isDriver ? <KV label="Catalog & prices on this phone"><Pill tone="green">Ready</Pill></KV> : null}
            </Section>

            {isDriver ? (
                <React.Fragment>
                    <Section title="Load check" right={<Pill tone={allLoaded ? 'green' : 'amber'}>{loadedCount}/{stops.length} loaded</Pill>}>
                        <div className="fo-loadbar">
                            <div className="fo-loadbar__top"><span>{veh ? veh.name + ' · ' + veh.type : 'No vehicle assigned'}</span><strong>{lb(weight)}{cap ? ' of ' + lb(cap) : ''}</strong></div>
                            {cap ? <Bar value={weight} max={cap} tone={over ? 'red' : weight / cap > 0.85 ? 'amber' : 'green'} /> : null}
                            {over ? <div className="fo-field__error">Over the vehicle capacity by {lb(weight - cap)}. Tell the dispatcher.</div> : null}
                        </div>
                        {stops.map((s) => (
                            <button key={s.id} type="button" className={'fo-checkrow' + (loaded[s.id] ? ' is-on' : '')} onClick={() => setLoaded({ ...loaded, [s.id]: !loaded[s.id] })}>
                                {loaded[s.id] ? <FaCheckSquare /> : <FaRegSquare />}
                                <div><strong>{s.invoiceNo} · {s.name}</strong><span>{lb(s.weightLb)} · {s.window}{s.ref.terms === 'COD' ? ' · COD' : ''}</span></div>
                            </button>
                        ))}
                        {!allLoaded ? <div className="fo-muted">Tick every invoice as you load it. If something is missing, message the dispatcher.</div> : null}
                    </Section>
                    <Section title="Vehicle check">
                        {[['Tires and lights are OK', tires, setTires, true], ['Fuel is enough for the route', fuel, setFuel, true], ['There is NEW damage on the vehicle', damage, setDamage, false]].map((r) => (
                            <button key={r[0]} type="button" className={'fo-checkrow' + ((r[3] ? r[1] : !r[1]) ? ' is-on' : ' is-bad')} onClick={() => r[2](!r[1])}>
                                {(r[3] ? r[1] : !r[1]) ? <FaCheckCircle /> : <FaRegSquare />}<div><strong>{r[0]}</strong></div>{r[1] ? <Pill tone={r[3] ? 'green' : 'red'}>Yes</Pill> : <Pill tone={r[3] ? 'red' : 'green'}>No</Pill>}
                            </button>
                        ))}
                        {!vehicleOk ? <Banner tone="warn">The office gets a message about the vehicle problem when you start.</Banner> : null}
                    </Section>
                </React.Fragment>
            ) : null}

            <Section title="Odometer (optional)">
                <Field label="Miles on the odometer" hint="Used for mileage reports. You can also skip it."><input className="fo-input" type="number" inputMode="numeric" value={odo} onChange={(e) => setOdo(e.target.value)} placeholder="e.g. 84,210" /></Field>
                {isDriver && !vehicleOk ? <Field label="What is wrong?"><TextArea value={note} onChange={setNote} rows={2} placeholder="Describe it for the office" /></Field> : null}
            </Section>
        </ScreenShell>
    )
}
